import React, { useState, useRef, useEffect, useMemo } from "react";
import { normalizeGuid } from "../../utils/guid";
import type { CommunityEntityResponse } from "../../api";

/**
 * DEV-867. Search-as-you-type picker for choosing one entity from a
 * potentially large list. Replaces oversized native &lt;select&gt; dropdowns
 * (e.g. parent reassignment among Rotary's 500+ districts).
 *
 * v1 scope (per the DEV-867 task description):
 *   - Substring search on name (case-insensitive)
 *   - Top 20 suggestions sorted: exact &gt; prefix &gt; contains, then natural
 *   - Click to select; selected entity renders as a pill with a clear button
 *   - Optional "empty option" (e.g. "Root — no parent") that's selectable
 *     when the input is blank
 *   - Optional excludeGuid to prevent self-parenting
 *
 * Out of v1 (tracked in DEV-867 description):
 *   - Keyboard navigation (arrow keys / Enter / Escape)
 *   - Server-side pagination (client-side filtering up to ~50k entities)
 *   - Recent / favorite pinning
 */
export interface EntityPickerProps {
  value: string;
  onChange: (guid: string) => void;
  entities: CommunityEntityResponse[];
  /** Hide this entity from results — used to prevent self-parenting. */
  excludeGuid?: string;
  /**
   * If set, an "empty / clear" affordance is offered with this label.
   * Selecting it calls onChange("").
   */
  emptyOptionLabel?: string;
  placeholder?: string;
  /**
   * Optional path-display function. If provided, the picker shows the
   * entity's full hierarchy ("Zone 33 / District 7930 / Springfield Rotary
   * Club") in suggestions and the selected pill. Falls back to .name when
   * the function returns null/empty.
   */
  getPathDisplay?: (entityGuid: string) => string | null;
  /** Optional formatter for entity_type → human label. Shown as a hint chip. */
  typeLabelFn?: (entityType: string) => string;
  /** Disable the input (e.g. while saving). */
  disabled?: boolean;
}

export default function EntityPicker(props: EntityPickerProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside the picker.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const selected = useMemo(() => {
    const norm = normalizeGuid(props.value);
    if (!norm) return null;
    return props.entities.find((e) => normalizeGuid(e.entityGuid) === norm) || null;
  }, [props.value, props.entities]);

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const exclNorm = props.excludeGuid ? normalizeGuid(props.excludeGuid) : "";

    const matches = props.entities.filter((e) => {
      if (exclNorm && normalizeGuid(e.entityGuid) === exclNorm) return false;
      const n = e.name.toLowerCase();
      return n.includes(q);
    });

    // Relevance: exact > starts-with > contains, then natural sort by name.
    matches.sort((a, b) => {
      const an = a.name.toLowerCase();
      const bn = b.name.toLowerCase();
      const aExact = an === q ? 0 : 1;
      const bExact = bn === q ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;
      const aPre = an.startsWith(q) ? 0 : 1;
      const bPre = bn.startsWith(q) ? 0 : 1;
      if (aPre !== bPre) return aPre - bPre;
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
    });

    return matches.slice(0, 20);
  }, [search, props.entities, props.excludeGuid]);

  const labelFor = (e: CommunityEntityResponse): string => {
    if (props.getPathDisplay) {
      const path = props.getPathDisplay(e.entityGuid);
      if (path) return path;
    }
    return e.name;
  };

  // Style tokens — match the rest of the surrounding form (CommunityEntitiesTab
  // uses inline styles; logical properties for RTL).
  const containerStyle: React.CSSProperties = { position: "relative", flex: 1 };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "5px 8px",
    fontSize: 12,
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    background: "var(--bg-primary)",
    color: "var(--text-primary)",
    boxSizing: "border-box",
  };
  const pillStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 8px",
    background: "var(--bg-hover)",
    border: "1px solid var(--border-subtle)",
    borderRadius: 6,
    fontSize: 12,
  };
  const dropdownStyle: React.CSSProperties = {
    position: "absolute",
    insetInlineStart: 0,
    insetInlineEnd: 0,
    top: "100%",
    background: "var(--bg-secondary)",
    border: "1px solid var(--border-default)",
    borderRadius: 6,
    maxHeight: 280,
    overflowY: "auto",
    zIndex: 1000,
    boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
    marginBlockStart: 2,
  };
  const itemStyle: React.CSSProperties = {
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: 12,
    borderBlockEnd: "1px solid var(--border-subtle)",
  };

  if (selected) {
    return (
      <div ref={containerRef} style={containerStyle}>
        <div style={pillStyle}>
          <span style={{ flex: 1 }}>
            {labelFor(selected)}
            {props.typeLabelFn && (
              <span style={{ opacity: 0.55, fontSize: 11, marginInlineStart: 6, color: "var(--text-tertiary)" }}>
                ({props.typeLabelFn(selected.entityType)})
              </span>
            )}
          </span>
          {!props.disabled && (
            <button
              type="button"
              onClick={() => { props.onChange(""); setSearch(""); }}
              title="Clear selection"
              style={{
                border: "none",
                background: "none",
                color: "var(--text-tertiary)",
                cursor: "pointer",
                fontSize: 14,
                lineHeight: 1,
                padding: "0 4px",
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  }

  const showEmptyAffordance = props.emptyOptionLabel && search.trim() === "";

  return (
    <div ref={containerRef} style={containerStyle}>
      <input
        type="text"
        value={search}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={props.placeholder ?? "Search…"}
        disabled={props.disabled}
        style={inputStyle}
      />
      {open && (suggestions.length > 0 || showEmptyAffordance) && (
        <div style={dropdownStyle}>
          {showEmptyAffordance && (
            <div
              onMouseDown={() => { props.onChange(""); setOpen(false); setSearch(""); }}
              style={{ ...itemStyle, fontStyle: "italic", color: "var(--text-tertiary)" }}
            >
              {props.emptyOptionLabel}
            </div>
          )}
          {suggestions.map((s) => (
            <div
              key={s.entityGuid}
              onMouseDown={() => { props.onChange(s.entityGuid); setSearch(""); setOpen(false); }}
              style={itemStyle}
              onMouseEnter={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = "var(--bg-hover)"; }}
              onMouseLeave={(ev) => { (ev.currentTarget as HTMLDivElement).style.background = ""; }}
            >
              <div>{labelFor(s)}</div>
              {props.typeLabelFn && (
                <div style={{ opacity: 0.55, fontSize: 10, marginBlockStart: 2, color: "var(--text-tertiary)" }}>
                  {props.typeLabelFn(s.entityType)}
                </div>
              )}
            </div>
          ))}
          {suggestions.length === 0 && !showEmptyAffordance && search.trim() !== "" && (
            <div style={{ ...itemStyle, opacity: 0.55, cursor: "default" }}>
              No matches
            </div>
          )}
        </div>
      )}
    </div>
  );
}
